import { useState } from "react";
import { cn, deterministicHue, initials } from "@/lib/utils";
import { resolvePerson } from "@/lib/roster";

interface Props {
  name: string;
  /** Optional explicit photo URL. When omitted, we look it up in the roster. */
  src?: string;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
}

const sizeMap = {
  xs: "h-6 w-6 text-micro",
  sm: "h-8 w-8 text-caption",
  md: "h-10 w-10 text-body",
  lg: "h-12 w-12 text-h4",
} as const;

export function Avatar({ name, src, size = "md", className }: Props) {
  const [failed, setFailed] = useState(false);
  const photo = src ?? resolvePerson(name)?.photo;

  if (photo && !failed) {
    return (
      <img
        src={photo}
        alt={name}
        className={cn(
          "inline-block rounded-full object-cover shrink-0 bg-muted",
          sizeMap[size],
          className
        )}
        onError={() => setFailed(true)}
      />
    );
  }

  const hue = deterministicHue(name);
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-full font-semibold text-white shrink-0",
        sizeMap[size],
        className
      )}
      style={{ background: `linear-gradient(135deg, hsl(${hue} 65% 55%), hsl(${(hue + 30) % 360} 65% 45%))` }}
      aria-hidden
    >
      {initials(name)}
    </span>
  );
}
